import {
  CommentAnchoring,
  ElementInstance,
  ElementInstanceState,
  Page,
  PageState,
  Project,
  SectionRef,
  SectionRefState,
  Space,
} from 'blocks/editor/model';
import {
  elementTreeDfs,
  shallowCloneElementState,
  unrefSection,
} from 'blocks/editor/model_helpers';
import { SelectionState } from 'blocks/editor/selection/states';
import { recordMap } from 'blocks/woot_woot/string_map';
import { computedFn } from 'mobx-utils';

type ThreadAnchoringMap = Map<
  string,
  { sel: SelectionState; anchoring: CommentAnchoring }
>;

/**
 * A threadId might be referenced multiple times in the project (e.g. due to
 * an error, concurrent changes, copy and paste).  But we only want to show
 * a comment in 1 place.
 **/
export const findThreadsInProject = computedFn(function (
  project: Project,
): ThreadAnchoringMap {
  const out: ThreadAnchoringMap = new Map();

  // We want the innermost thread reference to win.
  // So each visit function is written in the order:
  //   1. Write to the map
  //   2. Visit children  (last write to a map wins)

  function visitSref(sel: SelectionState & { section: SectionRef }) {
    for (const [thread, anchoring] of sel.section.commentThreads.entries()) {
      out.set(thread, { sel, anchoring });
    }
    const root = unrefSection(sel.section, project)?.element.current;
    for (const el of root ? elementTreeDfs(root) : []) {
      for (const [thread, anchoring] of el.commentThreads.entries()) {
        if (!sel.section.commentThreads.has(thread)) {
          // An ElementInstance might be used in multiple places (because of
          // SectionRefs).  But we only want to show a comment in 1 place.
          // So we show comments only when both the ElementInstace and the
          // parent SectionRef reference the thread.
          continue;
        }
        out.set(thread, {
          sel: { ...sel, element: el, nodeId: anchoring.nodeId },
          anchoring,
        });
      }
    }
  }

  function visitPage(sel: SelectionState & { page: Page }) {
    for (const [thread, anchoring] of sel.page.commentThreads.entries()) {
      out.set(thread, { sel, anchoring });
    }
    for (const section of sel.page.sections) {
      visitSref({ ...sel, section });
    }
    for (const subPage of sel.page.subPages) {
      visitPage({ ...sel, page: subPage });
    }
  }

  function visitSpace(sel: SelectionState & { space: Space }) {
    for (const [thread, anchoring] of sel.space.commentThreads.entries()) {
      out.set(thread, { sel, anchoring });
    }
    for (const page of sel.space.subPages) {
      visitPage({ ...sel, page });
    }
  }

  function visitProject() {
    for (const [thread, anchoring] of project.commentThreads.entries()) {
      out.set(thread, { sel: {}, anchoring });
    }
    visitPage({ space: 'home', page: project.homePage });
    for (const space of project.spaces) {
      visitSpace({ space });
    }
  }

  visitProject();
  return out;
});

export function deleteThreadFromProject(project: Project, threadId: string) {
  function visitThreads(
    commentThreads: Pick<Map<string, void>, 'has' | 'delete'>,
  ) {
    if (commentThreads.has(threadId)) {
      commentThreads.delete(threadId);
    }
  }

  function visitElement(el: ElementInstance) {
    visitThreads(el.commentThreads);
    for (const slot of el.slotOverrides.values()) {
      slot.items.forEach(visitElement);
    }
  }

  function visitSref(sref: SectionRef) {
    visitThreads(sref.commentThreads);
    const element = unrefSection(sref, project)?.element.current;
    element && visitElement(element);
  }

  function visitPage(page: Page) {
    visitThreads(page.commentThreads);
    for (const section of page.sections) visitSref(section);
    for (const subPage of page.subPages) visitPage(subPage);
  }

  function visitSpace(space: Space) {
    visitThreads(space.commentThreads);
    for (const page of space.subPages) visitPage(page);
  }

  visitThreads(project.commentThreads);
  visitPage(project.homePage);
  for (const space of project.spaces) visitSpace(space);
}

/**
 * Remove a set of comment threads from a given page.
 *
 * @param page The page to remove threads from.
 * @param threadsToRemove the list of threads to remove. If null, all threads
 * will be removed.
 * @returns A new page object, does not modify the one passed in.
 */
export function removeThreadsFromPage(
  page: PageState,
  threadsToRemove: ReadonlySet<string> | null,
): PageState {
  const updatedThreads = threadsToRemove
    ? removeKeys(page.commentThreads, threadsToRemove)
    : (recordMap() as typeof page.commentThreads);
  return {
    ...page,
    commentThreads: updatedThreads,
    sections: page.sections.map((s) =>
      removeThreadsFromSection(s, threadsToRemove),
    ),
    subPages: page.subPages.map((p) =>
      removeThreadsFromPage(p, threadsToRemove),
    ),
  };
}

/**
 * Removes all associated comment threads from a given `page`.
 * @param page The page to remove threads from.
 * @returns A new page object, does not modify the one passed in.
 */
export function removeAllThreadsFromPage(page: PageState): PageState {
  return removeThreadsFromPage(page, null);
}

/**
 * Remove a set of comment threads from a given section.
 * @param section The section to remove threads from.
 * @param threadsToRemove The list of threads to remove. If null, all threads
 * will be removed.
 * @returns A new section object, does not modify the one passed in.
 */
export function removeThreadsFromSection(
  section: SectionRefState,
  threadsToRemove: ReadonlySet<string> | null,
): SectionRefState {
  const updatedThreads = threadsToRemove
    ? removeKeys(section.commentThreads, threadsToRemove)
    : (recordMap() as typeof section.commentThreads);
  if (section.type === 'reference') {
    return {
      ...section,
      commentThreads: updatedThreads,
    };
  }
  return {
    ...section,
    commentThreads: updatedThreads,
    value: {
      ...section.value,
      element: {
        current: section.value.element.current
          ? shallowCloneElementState(
              removeThreadsFromElement(
                section.value.element.current,
                threadsToRemove,
              ),
            )
          : undefined,
      },
    },
  };
}

/**
 * Removes all associated comment threads from a given section.
 * @param section The section to remove threads from.
 * @returns A new section object, does not modify the one passed in.
 */
export function removeAllThreadsFromSection(
  section: SectionRefState,
): SectionRefState {
  return removeThreadsFromSection(section, null);
}

/**
 * Remove a set of comment threads from a given element.
 * @param element The element to remove threads from.
 * @param threadsToRemove The list of threads to remove. If null, all associated
 * threads will be removed.
 * @returns A new element object, does not modify the one passed in.
 */
export function removeThreadsFromElement(
  element: ElementInstanceState,
  threadsToRemove: ReadonlySet<string> | null,
): ElementInstanceState {
  const updatedThreads = threadsToRemove
    ? removeKeys(element.commentThreads, threadsToRemove)
    : (recordMap() as typeof element.commentThreads);
  return {
    ...element,
    commentThreads: updatedThreads,
    slotOverrides: recordMap(
      Array.from(element.slotOverrides.entries()).map(([k, v]) => [
        k,
        {
          ...v,
          items: v.items.map((i) =>
            removeThreadsFromElement(i, threadsToRemove),
          ),
        },
      ]),
    ),
  };
}

/**
 * Remove all associated comment threads from a given element.
 * @param element The element to remove threads from.
 * @returns A new element object, does not modify the one passed in.
 */
export function removeAllThreadsFromElement(
  element: ElementInstanceState,
): ElementInstanceState {
  return removeThreadsFromElement(element, null);
}

function removeKeys<T>(
  map: ReadonlyMap<string, T>,
  threadsToRemove: ReadonlySet<string>,
) {
  const newMap = new Map(map);
  for (const thread of threadsToRemove) {
    newMap.delete(thread);
  }
  return recordMap(newMap);
}
